/**
 * Merkezi Veritabanı Servisi
 * 
 * Bu dosya tüm veritabanı sorgularını merkezi bir yerde toplar.
 * İleride Supabase yerine başka bir veritabanı kullanıldığında,
 * sadece bu dosyayı güncellemek yeterli olacak.
 */

import { supabase } from './supabase';

// --- YARDIMCI FONKSİYONLAR ---
const safeUUID = (id) => {
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return id && uuidRegex.test(id) ? id : null;
};

// --- KULLANICI İŞLEMLERİ ---
export const userService = {
  // Kullanıcı kaydı
  async register(userData) {
    try {
      const cleanData = {
        name: userData.name,
        surname: userData.surname || '',
        email: userData.email,
        phone: userData.phone || '',
        password: userData.password, // Şifre hash'lenmeli (ileride)
        agency_id: safeUUID(userData.agencyId),
        is_agent: userData.isAgent || false,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('users')
        .insert([cleanData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('User registration error:', error);
      throw error;
    }
  },

  // Kullanıcı girişi (email/password)
  async login(email, password) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .eq('password', password) // Şifre hash'lenmeli (ileride)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('User login error:', error);
      return null;
    }
  },

  // Telefon numarası ile kullanıcı bulma
  async findByPhone(phone) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('phone', phone)
        .single();

      if (error) return null;
      return data;
    } catch (error) {
      console.error('Find user by phone error:', error);
      return null;
    }
  },

  // Google ile giriş (email ile kullanıcı bulma)
  async findByEmail(email) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (error) return null;
      return data;
    } catch (error) {
      console.error('Find user by email error:', error);
      return null;
    }
  },

  // Kullanıcı güncelleme
  async update(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', safeUUID(userId))
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('User update error:', error);
      throw error;
    }
  }
};

// --- ACENTE İŞLEMLERİ ---
export const agencyService = {
  // Tüm acenteleri getir
  async getAll() {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(agency => ({
        ...agency,
        logo: agency.logo || agency.logo_url || '',
      }));
    } catch (error) {
      console.error('Fetch agencies error:', error);
      return [];
    }
  },

  // Ajans kodu ile acente bulma
  async findByAccessCode(accessCode) {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .eq('access_code', accessCode)
        .eq('is_active', true)
        .single();

      if (error) return null;
      return data;
    } catch (error) {
      console.error('Find agency by access code error:', error);
      return null;
    }
  },

  // Acente sahibi girişi (telefon + 6 haneli şifre)
  async ownerLogin(phone, specialPassword) {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .eq('phone', phone)
        .eq('owner_password', specialPassword) // 6 haneli özel şifre
        .eq('is_active', true)
        .single();

      if (error) return null;
      return data;
    } catch (error) {
      console.error('Agency owner login error:', error);
      return null;
    }
  },

  // Acente ekleme
  async create(agencyData) {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .insert([agencyData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create agency error:', error);
      throw error;
    }
  },

  // Acente güncelleme
  async update(agencyId, updates) {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .update(updates)
        .eq('id', safeUUID(agencyId))
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update agency error:', error);
      throw error;
    }
  },

  // Acente silmə
  async delete(agencyId) {
    try {
      if (!safeUUID(agencyId)) return true;
      const { error } = await supabase
        .from('agencies')
        .delete()
        .eq('id', safeUUID(agencyId));

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Agentlik silmə xətası:', error);
      throw error;
    }
  },

  // Agentlik loqosunu yenilə (Cloudinary-yə yüklə)
  async updateLogo(agencyId, logoFile) {
    try {
      if (!safeUUID(agencyId)) throw new Error('Yanlış agentlik ID');
      
      // Cloudinary-yə yüklə
      const uploadResult = await storageService.uploadImage(logoFile, 'agencies/logos');
      
      // Veritabanında yenilə
      const { data, error } = await supabase
        .from('agencies')
        .update({ 
          logo: uploadResult.publicUrl,
          logo_url: uploadResult.publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', safeUUID(agencyId))
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Loqo yeniləmə xətası:', error);
      throw error;
    }
  },

  // ID ilə agentlik gətir
  async getById(agencyId) {
    try {
      if (!safeUUID(agencyId)) return null;
      
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .eq('id', safeUUID(agencyId))
        .single();

      if (error) return null;
      
      return {
        ...data,
        logo: data.logo || data.logo_url || '',
      };
    } catch (error) {
      console.error('Agentlik gətirmə xətası:', error);
      return null;
    }
  }
};

// --- İLAN İŞLEMLERİ ---
export const listingService = {
  // Bütün elanları gətir
  async getAll() {
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Təhlükəsiz məlumat çevrilməsi
      return (data || []).map(item => ({
        ...item,
        images: Array.isArray(item.images) ? item.images : [],
        amenities: Array.isArray(item.amenities) ? item.amenities : [],
        price: Number(item.price) || 0
      }));
    } catch (error) {
      console.error('Elanları gətirmə xətası:', error);
      return [];
    }
  },

  // İstifadəçinin öz elanlarını gətir
  async getByUserId(userId) {
    try {
      if (!safeUUID(userId)) return [];
      
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map(item => ({
        ...item,
        images: Array.isArray(item.images) ? item.images : [],
        amenities: Array.isArray(item.amenities) ? item.amenities : [],
        price: Number(item.price) || 0
      }));
    } catch (error) {
      console.error('İstifadəçi elanlarını gətirmə xətası:', error);
      return [];
    }
  },

  // Agentlik ID-sinə görə elanları gətir (agentlik sahibi üçün)
  async getByAgencyId(agencyId) {
    try {
      if (!safeUUID(agencyId)) return [];
      
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map(item => ({
        ...item,
        images: Array.isArray(item.images) ? item.images : [],
        amenities: Array.isArray(item.amenities) ? item.amenities : [],
        price: Number(item.price) || 0
      }));
    } catch (error) {
      console.error('Agentlik elanlarını gətirmə xətası:', error);
      return [];
    }
  },

  // Tək elan gətir (ID ilə)
  async getById(listingId) {
    try {
      if (!safeUUID(listingId)) return null;
      
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('id', listingId)
        .single();

      if (error) return null;

      return {
        ...data,
        images: Array.isArray(data.images) ? data.images : [],
        amenities: Array.isArray(data.amenities) ? data.amenities : [],
        price: Number(data.price) || 0
      };
    } catch (error) {
      console.error('Elan gətirmə xətası:', error);
      return null;
    }
  },

  // Elan yaratma
  async create(listingData) {
    try {
      const cleanData = {
        ...listingData,
        title: listingData.title || null,
        description: listingData.description || null,
        price: Number(listingData.price) || 0,
        rooms: listingData.rooms ? Number(listingData.rooms) : null,
        area: listingData.area ? Number(listingData.area) : null,
        floor: listingData.floor ? Number(listingData.floor) : null,
        total_floors: listingData.total_floors ? Number(listingData.total_floors) : null,
        location: listingData.location || null,
        address: listingData.address || listingData.location || null,
        phone: listingData.phone || null,
        
        // Yeni sahələr
        video_url: listingData.video_url || null,
        tamir_status: listingData.tamir_status || null,
        ipoteka: listingData.ipoteka || false,
        cixaris: listingData.cixaris || false,
        
        user_id: safeUUID(listingData.user_id),
        agency_id: safeUUID(listingData.agency_id),
        complex_id: safeUUID(listingData.complex_id),
        
        images: Array.isArray(listingData.images) ? listingData.images : [],
        amenities: Array.isArray(listingData.amenities) ? listingData.amenities : [],
        
        is_vip: listingData.is_vip || false,
        is_agency: listingData.is_agency || false,
        is_active: listingData.is_active !== undefined ? listingData.is_active : false,
      };

      const { data, error } = await supabase
        .from('listings')
        .insert([cleanData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Elan yaratma xətası:', error);
      throw error;
    }
  },

  // İlan güncelleme
  async update(listingId, updates) {
    try {
      const { data, error } = await supabase
        .from('listings')
        .update(updates)
        .eq('id', safeUUID(listingId))
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update listing error:', error);
      throw error;
    }
  },

  // Elan silmə
  async delete(listingId) {
    try {
      if (!safeUUID(listingId)) return true;
      const { error } = await supabase
        .from('listings')
        .delete()
        .eq('id', safeUUID(listingId));

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Elan silmə xətası:', error);
      throw error;
    }
  },

  // Elan yeniləmə (tam)
  async updateFull(listingId, listingData) {
    try {
      if (!safeUUID(listingId)) throw new Error('Yanlış elan ID');

      const cleanData = {
        title: listingData.title,
        description: listingData.description,
        type: listingData.type,
        category: listingData.category,
        price: Number(listingData.price) || 0,
        rooms: listingData.rooms ? Number(listingData.rooms) : null,
        area: listingData.area ? Number(listingData.area) : null,
        floor: listingData.floor ? Number(listingData.floor) : null,
        total_floors: listingData.total_floors ? Number(listingData.total_floors) : null,
        location: listingData.location,
        address: listingData.address,
        images: Array.isArray(listingData.images) ? listingData.images : [],
        amenities: Array.isArray(listingData.amenities) ? listingData.amenities : [],
        phone: listingData.phone,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('listings')
        .update(cleanData)
        .eq('id', safeUUID(listingId))
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Elan yeniləmə xətası:', error);
      throw error;
    }
  }
};

// --- KOMPLEKS İŞLEMLERİ ---
export const complexService = {
  // Tüm kompleksleri getir
  async getAll() {
    try {
      const { data, error } = await supabase
        .from('complexes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Fetch complexes error:', error);
      return [];
    }
  },

  // Kompleks ekleme
  async create(complexData) {
    try {
      const { data, error } = await supabase
        .from('complexes')
        .insert([complexData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create complex error:', error);
      throw error;
    }
  },

  // Kompleks güncelleme
  async update(complexId, updates) {
    try {
      const { data, error } = await supabase
        .from('complexes')
        .update(updates)
        .eq('id', safeUUID(complexId))
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update complex error:', error);
      throw error;
    }
  },

  // Kompleks silme
  async delete(complexId) {
    try {
      if (!safeUUID(complexId)) return true;
      const { error } = await supabase
        .from('complexes')
        .delete()
        .eq('id', safeUUID(complexId));

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Delete complex error:', error);
      throw error;
    }
  }
};

// --- STORAGE İŞLEMLERİ (Cloudinary) ---
export const storageService = {
  // Cloudinary üçün şəkil yükləmə
  async uploadImage(file, folder = 'listings') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'mydom_listings');
      formData.append('folder', folder);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'your-cloud-name'}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Şəkil yükləmə xətası');
      }

      const data = await response.json();
      return {
        filePath: data.public_id,
        publicUrl: data.secure_url,
        width: data.width,
        height: data.height,
      };
    } catch (error) {
      console.error('Şəkil yükləmə xətası:', error);
      throw error;
    }
  },

  // Cloudinary üçün video yükləmə
  async uploadVideo(file, folder = 'listings/videos') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'mydom_listings');
      formData.append('folder', folder);
      formData.append('resource_type', 'video');

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'your-cloud-name'}/video/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Video yükləmə xətası');
      }

      const data = await response.json();
      return {
        filePath: data.public_id,
        publicUrl: data.secure_url,
        duration: data.duration,
        width: data.width,
        height: data.height,
      };
    } catch (error) {
      console.error('Video yükləmə xətası:', error);
      throw error;
    }
  },

  // YouTube linkindən thumbnail götürmə (opsional)
  getYouTubeThumbnail(youtubeUrl) {
    try {
      const videoId = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
      if (!videoId) return null;
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    } catch {
      return null;
    }
  },

  // YouTube linkini embed formatına çevirmə
  getYouTubeEmbedUrl(youtubeUrl) {
    try {
      const videoId = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
      if (!videoId) return null;
      return `https://www.youtube.com/embed/${videoId}`;
    } catch {
      return null;
    }
  },

  // Şəkil silmə (Cloudinary)
  async deleteImage(publicId) {
    try {
      // Cloudinary silmə üçün backend API lazımdır, çünki signature tələb olunur
      // Bu hissəni backend-də həyata keçirmək daha yaxşıdır
      console.warn('Cloudinary silmə əməliyyatı backend-də həyata keçirilməlidir');
      return true;
    } catch (error) {
      console.error('Şəkil silmə xətası:', error);
      throw error;
    }
  }
};

// --- AUTHENTICATION İŞLEMLERİ ---
export const authService = {
  // Google ile giriş
  async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  },

  // Telefon OTP gönderme (hazırlık - henüz aktif değil)
  async sendOTP(phone) {
    try {
      // Bu kısım Supabase Auth'un telefon OTP özelliği ile entegre edilecek
      // Şimdilik placeholder olarak bırakıyoruz
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: phone,
        options: {
          channel: 'sms'
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Send OTP error:', error);
      throw error;
    }
  },

  // OTP doğrulama (hazırlık - henüz aktif değil)
  async verifyOTP(phone, token) {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: phone,
        token: token,
        type: 'sms'
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Verify OTP error:', error);
      throw error;
    }
  },

  // Oturum kontrolü
  async getSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    } catch (error) {
      console.error('Get session error:', error);
      return null;
    }
  },

  // Çıkış
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }
};

